'use client';

import { ChevronDown } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { Accordion } from 'radix-ui';

import {
  Reveal,
  REVEAL_DURATION,
  REVEAL_EASE,
  REVEAL_RISE,
  REVEAL_STEP,
  REVEAL_VIEWPORT,
} from '@/components/reveal';
import { AccentedTitle } from '@/components/section';
import { FAQ_COPY, FAQ_ITEMS } from '@/lib/content';
import { cn, FOCUS_RING_INSET } from '@/lib/utils';

import type { FAQPage, WithContext } from 'schema-dts';

const FAQ_SCHEMA: WithContext<FAQPage> = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_ITEMS.map(function toQuestion(item) {
    return {
      '@type': 'Question' as const,
      name: item.question,
      acceptedAnswer: { '@type': 'Answer' as const, text: item.answer },
    };
  }),
};

// Escaped, like every other inline JSON on the site: a literal `</script>` inside a value would
// otherwise close the tag. Stringified once at module scope rather than per render.
const FAQ_SCHEMA_JSON = JSON.stringify(FAQ_SCHEMA).replace(/</g, '\\u003c');

/**
 * `dl`/`dt`/`dd` rather than headings: these are question/answer pairs, and the markup keeps the
 * page's heading outline to one level per section. Radix drives the disclosure through `asChild`,
 * so the accordion's keyboard and ARIA behaviour sits on that markup instead of replacing it.
 *
 * A closed answer is not in the DOM — Radix mounts it on open so both the open and the close can
 * animate. `forceMount` would keep the text in the markup, but then "closed on load" and "closed
 * just now" are the same state in CSS, and the close animation runs on all eight on first paint.
 * Nothing that reads this page depends on the open one: the JSON-LD below carries every pair in
 * the format Google consumes for FAQPage, and `/llms.txt` renders them for AI crawlers.
 */
export function FaqSection() {
  const reducedMotion = useReducedMotion();

  return (
    <section id="faq" aria-labelledby="faq-title" className="scroll-mt-anchor">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: FAQ_SCHEMA_JSON }} />
      {/* A subhead, not a section title: the band's own heading is the pricing one above, and two
          display titles beside each other read as two sections that had collided. No kicker for
          the same reason — the 06 counts the band, and there is only one band. */}
      <Reveal>
        <h3 id="faq-title" className="text-2xl leading-display sm:text-3xl">
          <AccentedTitle text={FAQ_COPY.title} accent={FAQ_COPY.titleAccent} />
        </h3>
        <p className="mt-3 mb-8 text-base text-muted-foreground">{FAQ_COPY.subtitle}</p>
      </Reveal>
      {/* One at a time, and collapsible so the open one can be closed again. Single column: an
          accordion in two columns reflows the other column every time a panel opens. */}
      <Accordion.Root type="single" collapsible asChild>
        <dl className="border-t">
          {FAQ_ITEMS.map((item, index) => (
            <Accordion.Item key={item.question} value={item.question} asChild>
              {/* Motion directly rather than `Reveal`: a `dl` only allows one level of `div`
                  between it and the pairs, and the item already spends it. */}
              <motion.div
                className="border-b"
                data-reveal=""
                initial={reducedMotion ? false : { opacity: 0, y: REVEAL_RISE }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={REVEAL_VIEWPORT}
                transition={{
                  duration: REVEAL_DURATION,
                  ease: REVEAL_EASE,
                  delay: index * REVEAL_STEP,
                }}
              >
                <Accordion.Header asChild>
                  {/* Explicitly typed like a heading. It stays a dt for the outline, but the base
                      h1-h3 rule can't reach it, so the display face is asked for by hand. */}
                  <dt className="font-serif text-xl font-medium">
                    <Accordion.Trigger
                      className={cn(
                        'group flex w-full cursor-pointer items-start justify-between gap-6 py-5 text-start',
                        'transition-colors duration-200 hover:text-brand',
                        FOCUS_RING_INSET,
                      )}
                    >
                      {item.question}
                      <ChevronDown
                        aria-hidden="true"
                        className="mt-1 size-5 flex-none text-muted-foreground transition-transform duration-350 group-data-[state=open]:rotate-180"
                      />
                    </Accordion.Trigger>
                  </dt>
                </Accordion.Header>
                <Accordion.Content asChild>
                  <dd className="overflow-hidden data-[state=closed]:animate-accordion-close data-[state=open]:animate-accordion-open">
                    {/* The row spans the band; the prose inside it doesn't — a 1200px measure is
                        past the point where the eye finds the next line reliably. */}
                    <p className="max-w-[75ch] pb-6 text-base leading-relaxed text-muted-foreground">
                      {item.answer}
                    </p>
                  </dd>
                </Accordion.Content>
              </motion.div>
            </Accordion.Item>
          ))}
        </dl>
      </Accordion.Root>
    </section>
  );
}
